import { query } from "./strapi";
import { getNewsletter, NewsletterData } from "./getNewsletter";
import { getPartnerPromo, PartnerPromoData } from "./getPartnerPromo";

// Properly export the HomeInfoData interface so it can be imported
export interface HomeInfoData {
  homebanner?: any[];
  pageContent?: any[];
  newsletter?: NewsletterData;
  partnerPromo?: PartnerPromoData;
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    metaRobots?: string;
    canonicalURL?: string;
    keywords?: string;
    metaImage?: {
      url?: string;
    };
    openGraph?: {
      "og:title"?: string;
      "og:description"?: string;
      "og:image"?: {
        url?: string;
      };
      "og:url"?: string;
      "og:type"?: string;
    };
    structuredData?: any;
    metaViewport?: string;
  };
}

// Add default SEO data to ensure critical fields are always present
const DEFAULT_SEO = {
  metaTitle: "Evefant - Celebra todo",
  metaDescription:
    "Encuentra todo para tu fiesta y nosotros te lo llevamos. Desde decoración con globos, hasta los bocaditos. Delivery en todo Lima.",
  metaRobots: "index, follow",
  canonicalURL:
    typeof window !== "undefined"
      ? window.location.origin
      : "https://evefant.com",
  keywords:
    "evefant, celebraciones, fiestas, delivery, decoración, globos, bocaditos, lima",
  structuredData: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Evefant",
    description:
      "Encuentra todo para tu fiesta y nosotros te lo llevamos. Delivery en todo Lima.",
    url:
      typeof window !== "undefined"
        ? window.location.origin
        : "https://evefant.com",
  },
};

export async function getHomeInfo(): Promise<HomeInfoData> {
  try {
    // Get home structure data
    const homeRes = await query("home-structure");

    // Get newsletter and partner promo data
    const newsletterData = await getNewsletter();
    const partnerPromoData = await getPartnerPromo();

    // Get SEO data from the homeRes response - apply defaults for missing values
    let seoData = homeRes.seo || {};

    // If seo data exists but fields are missing, fill them with defaults
    if (seoData) {
      // Start with backend SEO data, then fill missing fields from DEFAULT_SEO
      seoData = {
        ...DEFAULT_SEO,
        ...seoData,
        // For openGraph, prioritize backend fields, but fill missing from defaults
        openGraph: {
          "og:title":
            seoData.openGraph?.["og:title"] ??
            seoData.metaTitle ??
            DEFAULT_SEO.metaTitle,
          "og:description":
            seoData.openGraph?.["og:description"] ??
            seoData.metaDescription ??
            DEFAULT_SEO.metaDescription,
          "og:image": seoData.openGraph?.["og:image"] ?? undefined,
          "og:url":
            seoData.openGraph?.["og:url"] ??
            seoData.canonicalURL ??
            DEFAULT_SEO.canonicalURL,
          "og:type": seoData.openGraph?.["og:type"] ?? "website",
        },
        // Merge structuredData: backend first, then default fills gaps
        structuredData: {
          ...(DEFAULT_SEO.structuredData || {}),
          ...(seoData.structuredData || {}),
        },
      };
    }

    return {
      ...homeRes,
      newsletter: newsletterData,
      partnerPromo: partnerPromoData,
      seo: seoData,
    };
  } catch (error) {
    console.error("Error fetching home info:", error);
    // Even on error, return a result with default SEO data
    return {
      seo: DEFAULT_SEO,
    };
  }
}
